import Link from 'next/link';
import { memo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { IconComponent } from '@/components/IconComponent';
import { ImageComponent } from '@/components/Image';
import { formatFileSize } from '@/modules/vip/Steps/components/VipTransfer/components/TransferRequestCard/components/VipTransferFormCard/components/VipFileUploader/helpers';
import { ACCEPTED_FILE_FORMATS } from '@/modules/vip/Steps/components/VipTransfer/components/TransferRequestCard/components/VipTransferFormCard/constants';
import { InfoTooltip } from './components/InfoTooltip';
import type { IVipFileError } from './helpers';
import { useVipFileUploader } from './hooks/useVipFileUploader';
import { ScVipFileUploader } from './styled';

interface IVipFileUploaderProps {
  title: string;
  file: File | null;
  onChange: (file: File | null) => void;
  withInfoIcon?: boolean;
  externalError?: string;
  isRequestPending: boolean;
  onFileError?: (error: IVipFileError | null) => void;
}

export const VipFileUploader = memo(
  ({
    title,
    file,
    onChange,
    withInfoIcon,
    externalError,
    isRequestPending,
    onFileError,
  }: IVipFileUploaderProps) => {
    const { t } = useTranslation('vip');
    const {
      isMobile,
      hiddenFileInput,
      displayError,
      getRootProps,
      getInputProps,
      handleFileInputChange,
      handleOpen,
      handleRemove,
    } = useVipFileUploader({ file, onChange, externalError, onFileError });

    const isShowFilePreview = file && !isRequestPending;

    return (
      <ScVipFileUploader $hasError={!!displayError} {...getRootProps()}>
        <div className="upload-place">
          <ImageComponent
            alt="uploadFileIcon"
            className="upload-icon"
            height={24}
            src="/icon-img/uploadFileIcon.svg"
            width={24}
          />
          <div className="title-wrapper">
            <span className="title">
              {title}
              {withInfoIcon && (
                <InfoTooltip isMobile={isMobile}>
                  <IconComponent
                    className="info-icon"
                    name="tooltipIcon"
                    size="20px"
                  />
                </InfoTooltip>
              )}
            </span>
          </div>

          <input {...getInputProps()} />

          <input
            accept={ACCEPTED_FILE_FORMATS}
            onChange={handleFileInputChange}
            onClick={(e) => e.stopPropagation()}
            ref={hiddenFileInput}
            style={{ display: 'none' }}
            type="file"
          />

          <p className="message">
            <Trans
              components={{
                l: <Link className="link" href="#" onClick={handleOpen} />,
              }}
              i18nKey="steps.vipTransfer.transferForm.message"
              t={t}
            />
          </p>
        </div>

        {isShowFilePreview && (
          <div className="selected-item-wrapper">
            <div className="selected">
              <ImageComponent
                alt="img-preview"
                height={24}
                src="/vip/img-preview.webp"
                width={24}
              />
              <span className="file-name">{file.name}</span>
              <span className="file-size">{formatFileSize(file.size)}</span>
            </div>
            <button onClick={handleRemove} type="button">
              <IconComponent
                className="trash-icon"
                name="trashIcon"
                size="24px"
              />
            </button>
          </div>
        )}

        {displayError && <span className="error">{displayError}</span>}
      </ScVipFileUploader>
    );
  }
);

VipFileUploader.displayName = 'VipFileUploader';
